import { isSignal, Resource, Signal } from '@angular/core';
import { getValue, Path, QueryFilter, queryFilter } from '@softline/core';
import { FilterItemConfig } from '../../../types/filter-config';
import { getQueryColumn } from '../../../helper/get-query-column.function';
import { PageFilterDataService } from './page-filter-data.service';

/**
 * Distinct filter values over rows the client already holds — an array, a signal
 * or a resource — so a page filter works without a value endpoint (in-memory
 * pages, tests, the design-system catalog).
 *
 * Mirrors what `PageFilterQueryDataService` asks the backend for: the distinct
 * values of the item's column under the other items' filter and the base filter,
 * sorted, searched and paged. Entity items yield the nested entity objects, since
 * that is what the multi-select input compares by id.
 */
export class PageFilterArrayDataService<
  T extends object,
> extends PageFilterDataService<T> {
  constructor(
    private readonly rows: T[] | Signal<T[]> | Resource<T[]>,
    private readonly baseFilter?: Signal<QueryFilter<T>>
  ) {
    super();
  }

  /** Distinct value count per item — what the query service reads as `totalcount`. */
  override async loadCount(
    items: FilterItemConfig<T>[],
    filter: QueryFilter<T> | null
  ): Promise<Record<Path<T>, number>> {
    const rows = this.restrict(filter);
    const counts = {} as Record<Path<T>, number>;
    for (const item of items)
      counts[getQueryColumn(item)] = this.distinct(rows, item).length;
    return counts;
  }

  override async loadItems(
    item: FilterItemConfig<T>,
    filter: QueryFilter<T> | null,
    start: number,
    end: number,
    search: string
  ): Promise<Partial<T>[]> {
    let values = this.distinct(this.restrict(filter), item);
    const term = search?.trim().toLowerCase();
    if (term)
      values = values.filter((value) => this.searchText(item, value).includes(term));
    return values.slice(start, end) as Partial<T>[];
  }

  private currentRows(): T[] {
    if (Array.isArray(this.rows)) return this.rows;
    if (isSignal(this.rows)) return this.rows() ?? [];
    return this.rows.value() ?? [];
  }

  private restrict(filter: QueryFilter<T> | null): T[] {
    let rows = [...this.currentRows()];
    const base = this.baseFilter?.();
    if (base) rows = queryFilter(rows, base);
    if (filter) rows = queryFilter(rows, filter);
    return rows;
  }

  private distinct(rows: T[], item: FilterItemConfig<T>): unknown[] {
    // entities are deduplicated by id, everything else by value
    const byKey = new Map<unknown, unknown>();
    for (const row of rows) {
      const value = getValue(row, item.field);
      if (value === null || value === undefined) continue;
      const key = item.type === 'entity' ? (value as { id: unknown }).id : value;
      if (!byKey.has(key)) byKey.set(key, value);
    }
    return [...byKey.values()].sort((a, b) => this.compare(item, a, b));
  }

  private compare(item: FilterItemConfig<T>, a: unknown, b: unknown): number {
    const left = this.sortValue(item, a);
    const right = this.sortValue(item, b);
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return `${left}`.localeCompare(`${right}`, undefined, { numeric: true });
  }

  private sortValue(item: FilterItemConfig<T>, value: unknown): unknown {
    if (item.type !== 'entity') return value;
    return getValue(value as object, item.sortField as never);
  }

  private searchText(item: FilterItemConfig<T>, value: unknown): string {
    if (item.type !== 'entity') return `${value}`.toLowerCase();
    const entity = value as object & { id: unknown };
    const fields = item.additionalFields.map((field) => getValue(entity, field as never));
    return [entity.id, ...fields].join(' ').toLowerCase();
  }
}
